import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { Book, Lightbulb, Code, Palette, Zap, Shield, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";

const sections = [
  {
    title: "Premiers pas",
    icon: Lightbulb,
    articles: [
      { title: "Introduction à Floop", description: "Découvrez ce que Floop peut faire pour vous" },
      { title: "Créer votre premier compte", description: "Guide étape par étape pour démarrer" },
      { title: "Comprendre les crédits", description: "Comment fonctionne le système de crédits" },
      { title: "Votre première génération", description: "Générez votre première image en 2 minutes" },
    ],
  },
  {
    title: "Génération d'images",
    icon: Palette,
    articles: [
      { title: "Écrire de bons prompts", description: "Conseils pour des résultats optimaux" },
      { title: "Floop Basic vs Floop Pro", description: "Choisir le bon modèle pour vos besoins" },
      { title: "Résolutions et formats", description: "Guide des tailles d'images disponibles" },
      { title: "Styles et effets", description: "Explorer les différents styles artistiques" },
    ],
  },
  {
    title: "Fonctionnalités avancées",
    icon: Zap,
    articles: [
      { title: "Mode Image vers Image", description: "Transformer vos images existantes" },
      { title: "Inpainting", description: "Modifier des zones spécifiques" },
      { title: "Upscaling", description: "Augmenter la résolution de vos images" },
      { title: "Floop Studio", description: "Utiliser l'éditeur intégré" },
    ],
  },
  {
    title: "API & Intégrations",
    icon: Code,
    articles: [
      { title: "Introduction à l'API", description: "Commencer avec l'API Floop" },
      { title: "Authentification", description: "Gérer vos clés API" },
      { title: "Endpoints", description: "Référence complète des endpoints" },
      { title: "Exemples de code", description: "Exemples dans différents langages" },
    ],
  },
  {
    title: "Compte & Facturation",
    icon: Shield,
    articles: [
      { title: "Gérer votre abonnement", description: "Changer de plan, annuler, etc." },
      { title: "Historique des paiements", description: "Consulter vos factures" },
      { title: "Acheter des crédits", description: "Recharger votre compte" },
      { title: "Paramètres du compte", description: "Personnaliser votre profil" },
    ],
  },
];

const Documentation = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-12 text-center">
        <div className="container mx-auto px-6">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Book className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display">
            Documentation
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Tout ce que vous devez savoir pour maîtriser Floop. Guides, tutoriels et références.
          </p>
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Rechercher dans la documentation..."
              className="pl-12 bg-input border-border rounded-xl"
            />
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="pb-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/tutorials" className="px-4 py-2 bg-secondary rounded-full text-sm text-muted-foreground hover:text-foreground transition-colors">
              Tutoriels vidéo
            </Link>
            <Link to="/api" className="px-4 py-2 bg-secondary rounded-full text-sm text-muted-foreground hover:text-foreground transition-colors">
              Référence API
            </Link>
            <Link to="/help" className="px-4 py-2 bg-secondary rounded-full text-sm text-muted-foreground hover:text-foreground transition-colors">
              Centre d'aide
            </Link>
            <Link to="/changelog" className="px-4 py-2 bg-secondary rounded-full text-sm text-muted-foreground hover:text-foreground transition-colors">
              Changelog
            </Link>
          </div>
        </div>
      </section>

      {/* Documentation Sections */}
      <section className="pb-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sections.map((section, index) => (
              <div key={index} className="bg-card border border-border rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-foreground">{section.title}</h2>
                </div>
                <ul className="space-y-3">
                  {section.articles.map((article, articleIndex) => (
                    <li key={articleIndex}>
                      <a href="#" className="block p-3 rounded-xl hover:bg-secondary transition-colors">
                        <div className="font-medium text-foreground text-sm">{article.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{article.description}</div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Help CTA */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Vous ne trouvez pas ce que vous cherchez ?
          </h2>
          <p className="text-muted-foreground mb-6">
            Notre équipe support est là pour vous aider.
          </p>
          <Link to="/contact">
            <button className="px-6 py-3 bg-gradient-floop text-white rounded-full font-medium hover:opacity-90 transition-opacity">
              Contacter le support
            </button>
          </Link>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default Documentation;
